'use client';

import classNames from 'classnames';
import { type ReactElement, useMemo, useState } from 'react';
import Image from '@alltrails/core/components/Image';
import Remove from '@alltrails/denali/icons/Remove';
import type Trail from '@alltrails/types/Trail';
import type TrailDetails from '@alltrails/types/TrailDetails';
import useCardPhotos from '../../hooks/useCardPhotos';
import type { CardProps } from '../../types';
import { getTrailUrl } from '../../utils/cardLinks';
import CardLink from '../CardLink';
import CompletionBadge from '../CompletionBadge';
import DragHandle from '../DragHandle';
import HeartButton from '../HeartButton';
import PhotoCarousel from '../PhotoCarousel';
import TrailMetadata from '../TrailMetadata';
import styles from './styles/styles.module.scss';

// https://www.figma.com/file/mh7ECfwLfHr8jTIaAYOS80/Trail-Cards-Handoff?type=design&node-id=6-22361&mode=design&t=3d9YZtUhEYpY0Cff-0

export type TrailStandardCardProps = Omit<CardProps, 'useMetric' | 'decimalPlaces'> & {
  trail: Trail | TrailDetails;
  url?: string; // Allow overriding of the default link to the trail page
  isCompleted?: boolean;
  isCompletionBadgeAutoExpanded?: boolean;
  isPhotoZoomEnabled?: boolean;
  isVerified?: boolean;
  onCompletionBadgeClick?: () => void;
  pinnedPhoto?: string;
  profilePhoto?: string;
  metadata?: ReactElement; // custom metadata element for trail cards in feed
  displayRank?: number; // 1-based index to display before the trail name
  blockNavigation?: boolean; // Prevent the trail links from actually navigating - the monolith handles all navigation from the onClick handlers
};

/**
 * TrailStandardCard is the standard card for a trail.
 *
 * This does not support any other variants. This is not also the hover or small or feed card. This is only the standard card.
 */
const TrailStandardCard = ({
  className,
  trail,
  url,
  languageRegionCode,
  onCardClick,
  onCardMouseEnter,
  onCardMouseLeave,
  onFavoriteClick,
  isFavorite,
  isCompleted,
  isCompletionBadgeAutoExpanded,
  isPhotoZoomEnabled,
  isVerified,
  openInNewTab,
  maxPhotos = 6,
  baseUrl = '',
  dragHandleProps,
  deleteButton,
  onCompletionBadgeClick,
  pinnedPhoto,
  profilePhoto,
  metadata,
  displayRank,
  blockNavigation
}: TrailStandardCardProps) => {
  const { name, slug, id } = trail;
  const href = url || getTrailUrl(slug, baseUrl, languageRegionCode);
  const { photos, loadPhotos } = useCardPhotos({ cardType: 'trails', id, size: '1080x700', maxPhotos: Math.min(maxPhotos, 6), profilePhoto });
  const [cardHovered, setCardHovered] = useState(false);

  const onMouseEnter = () => {
    setCardHovered(true);
    loadPhotos();
    onCardMouseEnter?.();
  };

  const onMouseLeave = () => {
    setCardHovered(false);
    onCardMouseLeave?.();
  };

  const metadataElement = metadata || <TrailMetadata trail={trail} baseUrl={baseUrl} openInNewTab={openInNewTab} displayRank={displayRank} />;

  const editMode = !!deleteButton;

  const leftOverlayElement = useMemo(() => {
    if (dragHandleProps) {
      return <DragHandle {...dragHandleProps} />;
    }
    return (
      <CompletionBadge
        isAutoExpanded={isCompletionBadgeAutoExpanded}
        isCompleted={isCompleted}
        isVerified={isVerified}
        onClick={onCompletionBadgeClick}
      />
    );
  }, [dragHandleProps, isCompleted, isCompletionBadgeAutoExpanded, isVerified, onCompletionBadgeClick]);

  const rightOverlayElement = useMemo(() => {
    if (deleteButton) {
      return (
        <button
          className={styles.deleteButton}
          onClick={deleteButton.onClick}
          aria-label={deleteButton.title}
          title={deleteButton.title}
          type="button"
        >
          <Remove color="--color-neutral-white" />
        </button>
      );
    }
    return onFavoriteClick ? <HeartButton isSelected={isFavorite || false} onClick={onFavoriteClick} /> : null;
  }, [deleteButton, isFavorite, onFavoriteClick]);

  return (
    <div className={classNames(styles.container, className)} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <CardLink
        href={href}
        blockNavigation={blockNavigation}
        openInNewTab={openInNewTab}
        onClick={onCardClick}
        isOverlay
        overlayFocusStyle="offset"
        testId={`${name}_OverlayLink`}
      >
        {name}
      </CardLink>
      <div className={styles.carousel}>
        <CardLink href={href} blockNavigation={blockNavigation} openInNewTab={openInNewTab} onClick={onCardClick} preventFocus>
          <PhotoCarousel
            arrowStyle="subtle-with-standard-arrows"
            photos={editMode && photos.length ? [photos[0]] : photos}
            loadPhotos={loadPhotos}
            zoomPhoto={isPhotoZoomEnabled && cardHovered}
            uniqueId={name}
            aspectRatio="trail-standard-card"
            lazyLoadImages
            hasPaginationGradient
          />
          {pinnedPhoto ? <Image src={pinnedPhoto} alt="" className={styles.pinnedPhoto} loading="lazy" unoptimized width={0} height={0} /> : null}
        </CardLink>
        {editMode ? <div className={styles.nonInteractiveOverlay} /> : null}
      </div>
      <div className={styles.overlays}>
        {leftOverlayElement}
        {rightOverlayElement}
      </div>
      <div className={styles.metadata}>{metadataElement}</div>
    </div>
  );
};

export default TrailStandardCard;
